import React, { useCallback, useState } from 'react';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
}

/**
 * Promise-based confirmation via the design-system `ConfirmDialog` — the house replacement for the native
 * `window.confirm()` / `alert()` (which look off-brand and can't be styled/themed). Returns a `confirm(opts)`
 * that resolves true/false, plus a `dialog` node to render once in the component.
 *
 *   const [confirm, confirmDialog] = useConfirm();
 *   const onDelete = async () => { if (!(await confirm({ title: 'Delete?', description: '…', variant: 'destructive' }))) return; … };
 *   return (<>… {confirmDialog}</>);
 */
export function useConfirm(): [(opts: ConfirmOptions) => Promise<boolean>, React.ReactNode] {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) => new Promise<boolean>((resolve) => setState({ ...opts, resolve })),
    [],
  );

  const dialog = state ? (
    <ConfirmDialog
      open
      onOpenChange={(o) => { if (!o) { state.resolve(false); setState(null); } }}
      title={state.title}
      description={state.description}
      confirmText={state.confirmText}
      cancelText={state.cancelText}
      variant={state.variant}
      onConfirm={() => state.resolve(true)}
    />
  ) : null;

  return [confirm, dialog];
}
